import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Heart, Home, LayoutGrid, List, ShoppingCart, ShoppingBag } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import QuantityStepper from '../components/QuantityStepper';
import ProductOfferNote from '../components/ProductOfferNote';
import ErrorState from '../components/ErrorState';
import usePageMetadata from '../hooks/usePageMetadata';
import { useCart } from '../context/CartContext';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import { focusRing, homeShell } from '../styles/ui';
import { productPriceView } from '../utils/offerPresentation';
import ProductImage from '../components/ProductImage';

// GET /favourites returns products "in the same order the customer saved them"
// (customerAppFavouritesController.js), so newest-first is that order reversed.
// There is no favouritedAt timestamp, so nothing here claims a date.
const SORTS = [
  { id: 'latest', label: 'Latest Added' },
  { id: 'oldest', label: 'Oldest Added' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
];

const sortProducts = (products, sort) => {
  const list = [...products];
  switch (sort) {
    case 'oldest':
      return list;
    case 'price-asc':
      return list.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    case 'price-desc':
      return list.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
    case 'latest':
    default:
      return list.reverse();
  }
};

const FavouriteCard = ({ product, view, quantity, onAddToCart, onSetQuantity, onToggleFavourite }) => {
  const { _id, sku, displayName, thumbnail, sellingUom, isInStock, catchWeight } = product;
  // This location's projection of the favourite: its offer can come and go
  // with the location while the favourite itself stays.
  const { price, compareAtPrice } = productPriceView(product);
  const productHref = `/products/${sku || _id}`;
  const list = view === 'list';

  return (
    <li
      className={`group relative bg-white rounded-[1.25rem] border border-emerald-100 shadow-soft hover:shadow-card transition-shadow overflow-hidden ${
        list ? 'flex flex-col sm:flex-row' : 'flex flex-col'
      }`}
    >
      <div className={`relative shrink-0 ${list ? 'sm:w-[15rem]' : ''}`}>
        <Link to={productHref} className={`block ${focusRing}`} aria-label={displayName}>
          <span
            className={`block bg-gradient-to-br from-emerald-50 to-emerald-100/50 overflow-hidden ${
              list ? 'h-44 sm:h-full sm:min-h-[9.5rem]' : 'aspect-[16/8]'
            }`}
          >
            <ProductImage src={thumbnail} alt={displayName} />
          </span>
          {/* Availability is the server's own isInStock (availableForSale > 0). */}
          {!isInStock && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-4 bg-gray-800/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full">
              Out of stock
            </span>
          )}
        </Link>

        {/* Sits above the image link so removing never opens the product. */}
        <button
          type="button"
          onClick={onToggleFavourite}
          aria-label={`Remove ${displayName} from favourites`}
          aria-pressed="true"
          className={`absolute z-10 top-3 right-3 w-10 h-10 rounded-full bg-white shadow-card flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${focusRing}`}
        >
          <Heart size={18} className="text-accent-500 fill-accent-500" aria-hidden="true" />
        </button>
      </div>

      {(() => {
        const action = isInStock ? (
          quantity > 0 ? (
            <QuantityStepper quantity={quantity} onChange={(qty) => onSetQuantity(_id, qty)} />
          ) : (
            <button
              type="button"
              onClick={onAddToCart}
              className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-gradient-primary text-white text-sm font-semibold shadow-glow-teal ${focusRing}`}
            >
              <ShoppingCart size={16} aria-hidden="true" />
              Add to Cart
            </button>
          )
        ) : (
          <button
            type="button"
            disabled
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-gray-100 text-gray-400 text-sm font-semibold cursor-not-allowed"
          >
            <ShoppingBag size={16} aria-hidden="true" />
            Out of Stock
          </button>
        );

        // Only rendered when the projection actually carries a selling UOM.
        const unit = sellingUom ? (
          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-md">
            {sellingUom}
          </span>
        ) : null;

        const name = (
          <Link to={productHref} className={`${focusRing} block`}>
            <h3 className="text-[15px] sm:text-base font-bold text-emerald-950 leading-snug line-clamp-2">
              {displayName}
            </h3>
          </Link>
        );

        const priceRow = (
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-emerald-950">₹{price ?? '—'}</span>
            {compareAtPrice !== null && (
              <span className="text-sm text-gray-400 line-through">₹{compareAtPrice}</span>
            )}
          </div>
        );

        const offerNote = <ProductOfferNote product={product} />;

        const catchNote = catchWeight ? (
          <p className="text-xs text-accent-600">Weight may vary — Pay on Delivery only</p>
        ) : null;

        if (list) {
          return (
            <div className="flex flex-1 flex-col sm:flex-row sm:items-center gap-4 p-4 sm:px-6 sm:py-5 min-w-0">
              <div data-testid="fav-info" className="min-w-0 flex-1 space-y-1.5">
                {name}
                {priceRow}
                {offerNote}
                {catchNote}
                {unit}
              </div>
              <div
                data-testid="fav-action"
                className="shrink-0 sm:w-[13rem] flex sm:justify-end items-center"
              >
                {action}
              </div>
            </div>
          );
        }

        return (
          <div className="p-4 sm:p-5 flex flex-col flex-1 min-w-0">
            {name}
            <div className="mt-1.5">{priceRow}</div>
            <ProductOfferNote product={product} className="mt-1.5" />
            {catchNote && <div className="mt-1">{catchNote}</div>}
            <div className="mt-auto pt-4 flex items-center justify-between gap-3">
              {unit || <span />}
              {action}
            </div>
          </div>
        );
      })()}
    </li>
  );
};

const CardSkeleton = () => (
  <li className="bg-white rounded-[1.25rem] border border-emerald-100 shadow-soft overflow-hidden animate-pulse">
    <div className="aspect-[16/8] bg-emerald-50" />
    <div className="p-5 space-y-3">
      <div className="h-4 w-3/4 rounded bg-emerald-50" />
      <div className="h-5 w-20 rounded bg-emerald-50" />
      <div className="flex items-center justify-between pt-2">
        <div className="h-6 w-14 rounded bg-emerald-50" />
        <div className="h-11 w-32 rounded-full bg-emerald-50" />
      </div>
    </div>
  </li>
);

const FavouritesPage = () => {
  const { addItem, setQuantity, quantityFor } = useCart();
  const { isFavourite, loaded, loadError, favouriteProducts, favouriteCount, refreshFavourites, toggleFavourite } =
    useFavourites();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Presentation state only — never sent to the API.
  const [view, setView] = useState('grid');
  const [sort, setSort] = useState('latest');

  usePageMetadata({
    title: 'Your Favourites | Fresh & Homely',
    description: 'Products you have saved at Fresh & Homely.',
    canonicalPath: '/favourites',
  });

  const visibleProducts = useMemo(
    () => favouriteProducts.filter((product) => isFavourite(product._id)),
    [favouriteProducts, isFavourite]
  );

  const sortedProducts = useMemo(() => sortProducts(visibleProducts, sort), [visibleProducts, sort]);

  // The backend keeps every favourite in `ids` but projects only products the
  // outlet delivering here sells. The rest are still favourites — just not
  // purchasable at this location — so say so rather than hiding them silently.
  const unavailableHereCount = Math.max(0, favouriteCount - visibleProducts.length);

  const handleToggleFavourite = async (product) => {
    try {
      const result = await toggleFavourite(product._id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return;
      }
      showToast('Removed from favourites', { tone: 'info' });
    } catch {
      // FavouritesContext rolls back its own optimistic update.
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
    showToast(`${product.displayName} added to cart`);
  };

  const showToolbar = loaded && !loadError && visibleProducts.length > 0;

  return (
    <>
      <Navbar />

      <main>
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-24 sm:pt-26 pb-6 sm:pb-7 bg-gradient-to-r from-emerald-50 via-emerald-50/70 to-white">
          <div className="pointer-events-none select-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <img
              src="/support-hero.png"
              alt=""
              className="hidden md:block absolute right-0 lg:right-[3%] -bottom-10 w-[17rem] lg:w-[21rem] xl:w-[24rem] object-contain"
            />
            <DecorLeaf className="hidden sm:block absolute left-4 top-24 w-12 text-emerald-500/60 -rotate-[24deg]" />
            <DecorLeaf className="hidden lg:block absolute right-[30%] top-16 w-10 text-emerald-500/50 rotate-[20deg]" />
            <DecorLeaf className="hidden lg:block absolute left-[46%] bottom-6 w-8 text-emerald-600/40 rotate-[36deg]" />
            <span className="hidden lg:flex absolute right-[26%] top-20 w-14 h-14 rounded-full bg-white shadow-card items-center justify-center">
              <Heart size={22} className="text-accent-500 fill-accent-500" />
            </span>
            <span className="hidden xl:block absolute right-[31%] top-[45%] serif italic text-emerald-800 text-lg leading-tight -rotate-[6deg]">
              Fresh Picks,
              <br />
              Always Yours
            </span>
          </div>

          <div className={`${homeShell} relative`}>
            <nav aria-label="Breadcrumb" className="mb-3">
              <ol className="flex items-center gap-1.5 text-sm text-ink-secondary">
                <li>
                  <Link to="/" className={`inline-flex items-center gap-1.5 hover:text-emerald-700 ${focusRing}`}>
                    <Home size={15} aria-hidden="true" />
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight size={14} />
                </li>
                <li aria-current="page" className="font-medium text-emerald-800">
                  Favourites
                </li>
              </ol>
            </nav>
            <h1 className="serif text-4xl sm:text-5xl text-emerald-950 leading-tight">Your Favourites</h1>
            <p className="text-base text-ink-secondary mt-2">Products you&apos;ve saved for later.</p>
          </div>
        </section>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <section className="pt-8 sm:pt-10 pb-12 sm:pb-14 bg-white min-h-[40vh]">
          <div className={homeShell}>
            {showToolbar && (
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-emerald-950">
                  {visibleProducts.length} Saved Item{visibleProducts.length === 1 ? '' : 's'}
                </h2>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 p-1 rounded-xl bg-emerald-50/70 border border-emerald-100">
                    {[
                      { id: 'grid', icon: LayoutGrid, label: 'Grid view' },
                      { id: 'list', icon: List, label: 'List view' },
                    ].map(({ id, icon: Icon, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setView(id)}
                        aria-label={label}
                        aria-pressed={view === id}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${focusRing} ${
                          view === id ? 'bg-emerald-600 text-white' : 'text-emerald-700 hover:bg-white'
                        }`}
                      >
                        <Icon size={18} aria-hidden="true" />
                      </button>
                    ))}
                  </div>

                  <label htmlFor="favourites-sort" className="text-sm text-ink-secondary">
                    Sort by
                  </label>
                  <select
                    id="favourites-sort"
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                    className={`h-10 px-3 pr-8 rounded-xl border border-emerald-100 bg-white text-sm font-medium text-emerald-950 shadow-soft ${focusRing}`}
                  >
                    {SORTS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {!loaded && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 3 }).map((_, index) => (
                  <CardSkeleton key={index} />
                ))}
              </ul>
            )}

            {loaded && loadError && (
              <ErrorState
                message={loadError.message || 'Unable to load your favourites'}
                onRetry={refreshFavourites}
              />
            )}

            {loaded && !loadError && unavailableHereCount > 0 && (
              <p
                data-testid="favourites-unavailable-here"
                className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5"
              >
                {unavailableHereCount === 1
                  ? '1 of your favourites isn’t available at your delivery location.'
                  : `${unavailableHereCount} of your favourites aren’t available at your delivery location.`}{' '}
                They stay in your favourites.
              </p>
            )}

            {loaded && !loadError && visibleProducts.length === 0 && unavailableHereCount === 0 && (
              <div className="max-w-md mx-auto text-center py-12">
                <span className="w-16 h-16 rounded-full bg-emerald-50 text-accent-500 flex items-center justify-center mx-auto mb-4">
                  <Heart size={28} aria-hidden="true" />
                </span>
                <h2 className="serif text-2xl text-emerald-950 mb-2">No favourites yet</h2>
                <p className="text-sm text-ink-secondary mb-6">
                  Save products you love and find them here.
                </p>
                <Link
                  to="/categories"
                  className={`inline-flex items-center h-11 px-6 rounded-full bg-gradient-primary text-white text-sm font-semibold shadow-glow-teal ${focusRing}`}
                >
                  Browse Products
                </Link>
              </div>
            )}

            {loaded && !loadError && sortedProducts.length > 0 && (
              <ul
                data-testid="favourites-list"
                className={
                  view === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5'
                    : 'flex flex-col gap-4 max-w-5xl mx-auto'
                }
              >
                {sortedProducts.map((product) => (
                  <FavouriteCard
                    key={product._id}
                    product={product}
                    view={view}
                    quantity={quantityFor(product._id)}
                    onAddToCart={() => handleAddToCart(product)}
                    onSetQuantity={setQuantity}
                    onToggleFavourite={() => handleToggleFavourite(product)}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default FavouritesPage;
